import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Globe, ShieldCheck, Link2, TrendingUp, Sun, ArrowRightLeft, BadgeCheck,
  CheckCircle2, Ship, Landmark, BarChart3, Building2, ArrowRight, Send,
  CheckCircle, Plus, Minus, ChevronRight
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1643472018814-d9981b44162d?w=1920&q=85&auto=format&fit=crop';
const SOLAR_IMG = 'https://images.unsplash.com/photo-1680355065203-43ad84bb6e69?w=1400&q=85&auto=format&fit=crop';

const CountUp = ({ end, decimals = 0, duration = 1800, suffix = '' }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setVal(end); return; }
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(end * eased);
          if (p < 1) requestAnimationFrame(tick);
          else setVal(end);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{val.toFixed(decimals)}{suffix}</span>;
};

const whatWeDo = [
  { num: '01', title: 'Sourcing', icon: Globe, text: 'I-RECs, Indian RECs, Guarantees of Origin and other EACs from vetted generators, chosen to match your grid region, vintage and reporting year.' },
  { num: '02', title: 'Due diligence', icon: ShieldCheck, text: 'We check each batch for generation proof, issuance status, plant age and registry history, so you only buy attributes that hold up under audit.' },
  { num: '03', title: 'Matching & retirement', icon: Link2, text: 'Volumes matched to your site-level consumption, then retired in your name on the official registry with a redemption statement for every MWh.' },
  { num: '04', title: 'Strategy & PPAs', icon: TrendingUp, text: 'When the numbers support it, we help you move beyond certificates to green open access, captive solar or long-term PPAs.' }
];

const howSteps = [
  { title: 'Generate', icon: Sun, text: 'A renewable plant produces 1 MWh and the registry issues one certificate.' },
  { title: 'Transfer', icon: ArrowRightLeft, text: 'The certificate moves to your account through a verified trade.' },
  { title: 'Retire', icon: BadgeCheck, text: 'It is redeemed in your name for a set period and site, and can never be resold.' }
];

const checklist = [
  { title: 'Traceable', text: 'The MWh was really generated by an identified, registered plant, and issued only once.' },
  { title: 'Matched', text: 'Same market boundary, same reporting year and enough volume to cover your consumption.' },
  { title: 'Retired on record', text: 'Redemption is logged on the official registry with a statement your auditor can verify.' },
  { title: 'Exclusive', text: 'No other buyer, utility or government programme claims the same attribute.' }
];

const audience = [
  { title: 'Exporters and suppliers', icon: Ship, text: 'answering green-power questions from global buyers with RE100 or SBTi targets.' },
  { title: 'Listed companies', icon: Landmark, text: 'disclosing Scope 2 under BRSR and BRSR Core.' },
  { title: 'CDP and EcoVadis respondents', icon: BarChart3, text: 'who want renewable electricity to lift their score.' },
  { title: "Multinationals' Indian operations", icon: Building2, text: 'that must align local claims with group-level targets.' }
];

const workSteps = [
  { title: 'Consumption review', text: 'We map your electricity use by site, grid region and reporting year.' },
  { title: 'Certificate plan', text: 'You get a recommended mix, volume and price range, with the trade-offs explained.' },
  { title: 'Procure and retire', text: 'We buy from vetted sources and redeem in your name on the registry.' },
  { title: 'Claim pack', text: 'You receive redemption statements and a summary ready for your auditor, CDP or BRSR filing.' }
];

const faqs = [
  { q: 'What is an Energy Attribute Certificate?', a: 'A tradable proof that 1 MWh of electricity came from a renewable source. I-RECs are the international standard; India also has its own RECs, and Europe uses Guarantees of Origin.' },
  { q: 'Will EACs really lower our emissions figure?', a: 'Yes, for market-based Scope 2. When certificates are correctly matched and retired, the covered electricity is reported at zero emissions. Your location-based figure stays the same.' },
  { q: "What's the difference between location-based and market-based Scope 2?", a: 'Location-based uses the average emissions of your local grid. Market-based reflects the power you have contracted or claimed, including certificates. GHG Protocol asks companies to report both.' },
  { q: 'I-REC or Indian REC — which should we buy?', a: 'It depends on who you report to. International frameworks such as RE100 and CDP commonly accept I-RECs; domestic obligations may call for Indian RECs. We recommend the mix that fits your disclosures.' },
  { q: 'Do certificates count for RE100, CDP and BRSR?', a: 'High-quality, correctly retired certificates support RE100 renewable-electricity claims and CDP and BRSR Scope 2 disclosures. RE100 also applies its own technical criteria, such as plant age, which we screen for.' },
  { q: 'How quickly can we retire certificates for this reporting year?', a: 'Usually within 2–3 weeks of order confirmation, subject to registry timelines and vintage availability.' }
];

const EnergyAttributeCertificatesPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', company: '', usage: '', locations: '', stage: '', message: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollToEnquiry = () => {
    const el = document.getElementById('enquiry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const details = [
        `Annual electricity use (MWh): ${form.usage || 'N/A'}`,
        `Stage with EACs: ${form.stage || 'N/A'}`,
        '',
        form.message
      ].join('\n');
      await axios.post(`${API}/contact`, {
        name: form.name,
        organization: form.company,
        email: form.email,
        country: form.locations,
        project_type: 'Energy Attribute Certificates (EAC)',
        message: details
      });
      setSuccess(true);
      setForm({ name: '', email: '', company: '', usage: '', locations: '', stage: '', message: '', consent: false });
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      console.error('EAC enquiry error:', err);
      alert('There was an error submitting your enquiry. Please try again or email info@creduce.tech');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 bg-white border border-zinc-300 text-[#09090B] focus:outline-none focus:ring-1 focus:ring-[#047857] focus:border-[#047857] placeholder:text-zinc-400 transition-colors';
  const labelClass = 'block text-sm font-semibold text-[#09090B] mb-2';

  return (
    <div className="eac-page bg-white" data-testid="eac-page">
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        <img
          src={HERO_IMG}
          alt="Wind turbines and solar panels across a renewable energy landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, rgba(3,28,20,0.90) 0%, rgba(3,28,20,0.70) 38%, rgba(3,28,20,0.40) 70%, rgba(3,28,20,0.25) 100%)' }}
        />
        <div className="container mx-auto relative z-10 py-28">
          <div className="max-w-2xl">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/services" className="hover:text-white">Services</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Energy Attribute Certificates</span>
            </nav>
            <span className="overline" style={{ color: '#5EEAD4' }}>Renewable Energy Certificates</span>
            <h1 className="text-white mt-5 mb-7 leading-[1.02]">Green electricity claims that stand up to scrutiny.</h1>
            <p className="text-lg md:text-xl text-white/85 mb-9 leading-relaxed max-w-xl" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.25)' }}>
              CREDUCE sources, verifies and retires Energy Attribute Certificates — I-RECs, Indian RECs and international equivalents — matched to where and when you consume power. The result: a lower market-based Scope 2 figure that your auditor, CDP and RE100 will accept.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <button onClick={scrollToEnquiry} className="btn-primary" data-testid="eac-hero-quote-btn">
                <span>Get a certificate quote</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-[15px] font-semibold text-white border border-white/60 hover:bg-white hover:text-[#044e37] transition-colors backdrop-blur-sm" data-testid="eac-hero-advisor-btn">
                Talk to an advisor
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-white/75">
              <span>GHG Protocol aligned</span>
              <span className="w-1 h-1 rounded-full bg-white/50" />
              <span>RE100 &amp; CDP ready</span>
              <span className="w-1 h-1 rounded-full bg-white/50" />
              <span>BRSR compatible</span>
            </div>
          </div>
        </div>
      </section>

      {/* Impact stats band */}
      <section className="bg-[#047857] text-white py-14 md:py-16" data-testid="eac-hero-stats">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14">
            <div className="lg:w-56 flex-shrink-0">
              <div className="w-10 h-[3px] bg-[#5EEAD4] mb-4" />
              <p className="text-xs uppercase tracking-[0.24em] text-white/70">Since 2022</p>
              <p className="text-sm text-white/70 mt-3 leading-relaxed">Our global impact, independently verifiable on official registries.</p>
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-6 border-t border-white/20 pt-8 lg:border-t-0 lg:border-l lg:border-white/20 lg:pt-0 lg:pl-14">
              <div>
                <div className="font-display text-6xl md:text-7xl font-extrabold leading-none text-white">
                  <CountUp end={6} suffix="M" /><span className="text-[#5EEAD4]">*</span>
                </div>
                <p className="text-base text-white/85 mt-4 font-medium">I-RECs issued, traded &amp; redeemed globally</p>
              </div>
              <div>
                <div className="font-display text-6xl md:text-7xl font-extrabold leading-none" style={{ color: '#5EEAD4' }}>
                  <CountUp end={4.3} decimals={1} suffix="M" /><span className="text-white">*</span>
                </div>
                <p className="text-base text-white/85 mt-4 font-medium">Tonnes of CO₂ emissions directly avoided</p>
              </div>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-white/50 mt-10 max-w-5xl">
            *CO₂ avoided is calculated using the Central Electricity Authority's all-India weighted average grid emission factor of 0.710 tCO₂/MWh (CO₂ Baseline Database v21.0, FY 2024-25). Figures cover certificates retired from January 2022 to July 2026.
          </p>
        </div>
      </section>

      {/* Section 1 — What we do */}
      <section id="services" className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-5">From purchase to proof, handled end to end.</h2>
            <p className="text-lg text-[#52525B]">
              Buying a certificate is easy. Buying the right one, in the right market, for the right period — and proving it later — is where most claims fall apart. We take care of every step.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 auto-rows-fr border-t border-l border-zinc-200 bg-white">
            {whatWeDo.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.num} className="p-8 border-b border-r border-zinc-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`eac-service-${c.num}`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 bg-[#047857]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#047857]" strokeWidth={1.75} />
                    </div>
                    <span className="font-display text-2xl font-extrabold text-zinc-300">{c.num}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#09090B] mb-3">{c.title}</h3>
                  <p className="text-[15px] text-[#52525B] leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 2 — Market proof point */}
      <section className="py-20 md:py-28 bg-[#044e37] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="font-display text-6xl md:text-7xl font-extrabold mb-4" style={{ color: '#5EEAD4' }}>570 TWh+</div>
              <p className="text-lg text-white/85">Annual electricity use of RE100 companies — more than South Korea's entire grid.</p>
            </div>
            <div>
              <p className="text-lg text-white/85 leading-relaxed mb-5">
                Over 430 of the world's largest companies have committed to 100% renewable electricity through RE100, and their suppliers are next in line. For Indian exporters and manufacturers, credible green-power claims are fast becoming a condition of doing business.
              </p>
              <a
                href="https://www.theclimategroup.org/our-work/press/re100s-electricity-consumption-overtakes-south-koreas-calls-action-ahead-11th-basic"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/60 hover:text-white underline"
              >
                Source: The Climate Group, February 2025
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — How EACs work */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="accent-line mb-6" />
              <h2 className="text-[#09090B] mb-6">1 MWh generated. 1 certificate issued. 1 claim made.</h2>
              <p className="text-lg text-[#52525B] leading-relaxed mb-8">
                Every time a registered solar, wind or hydro plant feeds one megawatt-hour into the grid, a certificate is issued for it. When you buy that certificate and retire it against your own consumption, the renewable attribute is yours — and you report that electricity as zero-emission under the market-based method of GHG Protocol Scope 2. The physical electrons don't change; the ownership of the clean claim does.
              </p>
              <div className="space-y-4">
                {howSteps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex gap-4 items-start border-l-2 border-[#047857] pl-5 py-1">
                      <Icon className="w-6 h-6 text-[#047857] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                      <div>
                        <h4 className="text-base font-bold text-[#09090B] mb-1">{i + 1}. {s.title}</h4>
                        <p className="text-[15px] text-[#52525B]">{s.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-[#047857] z-10" />
              <img src={SOLAR_IMG} alt="Aerial view of a solar plant" className="w-full h-[400px] md:h-[480px] object-cover border border-zinc-200 shadow-soft" loading="lazy" />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-[#0EA5E9] z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 — Quality checklist */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B]">Four checks every certificate must pass.</h2>
          </div>
          <div className="grid md:grid-cols-2 border-t border-l border-zinc-200 bg-white max-w-5xl">
            {checklist.map((c, i) => (
              <div key={i} className="p-8 border-b border-r border-zinc-200 flex gap-4" data-testid={`eac-check-${i}`}>
                <CheckCircle2 className="w-7 h-7 text-[#047857] flex-shrink-0" strokeWidth={1.75} />
                <div>
                  <h3 className="text-lg font-bold text-[#09090B] mb-2">{c.title}</h3>
                  <p className="text-[15px] text-[#52525B] leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xl md:text-2xl text-[#09090B] italic text-center max-w-3xl mx-auto mt-12 leading-relaxed">
            Miss one of these and your Scope 2 number becomes a liability. Pass all four and it becomes a disclosure you can defend.
          </p>
        </div>
      </section>

      {/* Section 5 — Who it's for */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B]">Built for companies that report.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 auto-rows-fr border-t border-l border-zinc-200">
            {audience.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} className="p-8 border-b border-r border-zinc-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`eac-audience-${i}`}>
                  <div className="w-12 h-12 bg-[#047857]/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#047857]" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-base font-bold text-[#09090B] mb-2">{a.title}</h3>
                  <p className="text-[15px] text-[#52525B] leading-relaxed">{a.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 6 — How we work */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B]">Four steps, about 3–4 weeks.</h2>
          </div>
          <div className="grid md:grid-cols-4 border-t border-l border-zinc-200 bg-white">
            {workSteps.map((s, i) => (
              <div key={i} className="p-8 border-b border-r border-zinc-200" data-testid={`eac-step-${i}`}>
                <div className="font-display text-5xl font-extrabold text-[#047857]/20 mb-4">{`0${i + 1}`}</div>
                <h3 className="text-lg font-bold text-[#09090B] mb-2">{s.title}</h3>
                <p className="text-[15px] text-[#52525B] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Why CREDUCE */}
      <section className="py-20 md:py-28 bg-[#044e37] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="w-12 h-[3px] bg-[#5EEAD4] mb-6" />
            <h2 className="text-white mb-6">Carbon-market experience, applied to clean power.</h2>
            <p className="text-lg text-white/85 leading-relaxed">
              Since 2021, CREDUCE has developed, monitored and traded environmental commodities for corporates and governments, including a carbon-credit partnership with the Government of Gujarat. We bring the same discipline to renewable energy certificates: clear sourcing, documented checks and one point of contact from first quote to final retirement.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8 — FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-12">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B]">Common questions</h2>
          </div>
          <div className="max-w-3xl border-t border-zinc-200">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-zinc-200">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                  data-testid={`eac-faq-${i}`}
                >
                  <span className="text-base md:text-lg font-bold text-[#09090B]">{f.q}</span>
                  {openFaq === i ? <Minus className="w-5 h-5 text-[#047857] flex-shrink-0" /> : <Plus className="w-5 h-5 text-[#047857] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <p className="text-[#52525B] leading-relaxed pb-6 pr-8">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 9 — Related reading */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-10">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B]">Learn more</h2>
          </div>
          <div className="grid md:grid-cols-[1fr_2fr] border border-zinc-200 bg-white max-w-5xl">
            <img src={SOLAR_IMG} alt="Renewable energy certificates guide" className="w-full h-56 md:h-full object-cover" loading="lazy" />
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <h3 className="text-xl font-bold text-[#09090B] mb-3">I-RECs explained: how renewable energy certificates reduce your Scope 2</h3>
              <p className="text-[#52525B] mb-5 leading-relaxed">A plain-language guide to how I-RECs work, what makes them credible and how to avoid the mistakes auditors flag. 8 min read.</p>
              <button onClick={scrollToEnquiry} className="text-[#047857] font-semibold text-sm flex items-center gap-2 self-start hover:gap-3 transition-all">
                Read the guide
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10 — Enquiry */}
      <section id="enquiry" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            {/* Form */}
            <div>
              <div className="accent-line mb-6" />
              <h2 className="text-[#09090B] mb-4">Ready to green your electricity claim?</h2>
              <p className="text-lg text-[#52525B] mb-8">
                Share your annual consumption and operating locations. We'll come back with a certificate plan and indicative pricing.
              </p>
              {success ? (
                <div className="border border-zinc-200 p-10 text-center" data-testid="eac-form-success">
                  <div className="w-16 h-16 bg-[#047857] flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#09090B] mb-3">Thank you</h3>
                  <p className="text-[#52525B]">A CREDUCE specialist will reply within one business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="eac-enquiry-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>Full name *</label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} required className={inputClass} placeholder="Your name" data-testid="eac-input-name" />
                    </div>
                    <div>
                      <label className={labelClass}>Work email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} placeholder="you@company.com" data-testid="eac-input-email" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>Company *</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} required className={inputClass} placeholder="Company name" data-testid="eac-input-company" />
                    </div>
                    <div>
                      <label className={labelClass}>Annual electricity use (MWh)</label>
                      <input type="number" name="usage" value={form.usage} onChange={handleChange} className={inputClass} placeholder="e.g. 12000" data-testid="eac-input-usage" />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Countries / states of operation</label>
                    <input type="text" name="locations" value={form.locations} onChange={handleChange} className={inputClass} placeholder="e.g. Gujarat, Maharashtra" data-testid="eac-input-locations" />
                  </div>
                  <div>
                    <label className={labelClass}>Where are you with EACs?</label>
                    <select name="stage" value={form.stage} onChange={handleChange} className={inputClass} data-testid="eac-select-stage">
                      <option value="">Select an option</option>
                      <option value="Just exploring">Just exploring</option>
                      <option value="Bought before, want better quality">Bought before, want better quality</option>
                      <option value="Need certificates for this reporting year">Need certificates for this reporting year</option>
                      <option value="Considering PPAs">Considering PPAs</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4} className={`${inputClass} resize-none`} placeholder="Tell us about your requirement..." data-testid="eac-input-message" />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-[#52525B]">
                    <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} required className="mt-1" data-testid="eac-input-consent" />
                    <span>I agree to CREDUCE's Privacy Policy.</span>
                  </label>
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="eac-submit-btn">
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <span>Request a quote</span>
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnergyAttributeCertificatesPage;
